import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    background: theme.palette.mode === 'light'
      ? '#FFFFFF'
      : 'linear-gradient(145deg, #121212 0%, #1a1a1a 100%)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 4px 20px 0 rgba(0, 0, 0, 0.1)'
      : '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
    border: theme.palette.mode === 'light'
      ? '1px solid rgba(0, 0, 0, 0.1)'
      : '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
  },
}));

const StyledImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  borderRadius: '8px',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

const GradientText = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(45deg, ${
    theme.palette.primary.main
  } 30%, ${
    theme.palette.primary.light
  } 90%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 700,
}));

const HistoryDialog = ({
  open,
  onClose,
  isImagesLoading,
  selectedImages,
  onOpenZoom,
}) => {
  // Função para ordenar as imagens
  const ordenarImagens = (imagens) => {
    const ordemDesejada = {
      'Local': 1,
      'EPI': 2,
      'Desligar': 3,
      'Bloquear': 4,
      'Testar': 5,
      'Aterrar': 6,
      'Proteger': 7,
      'Sinalizar': 8
    };

    // Primeiro, agrupar imagens por tipo
    const imagensPorTipo = imagens.reduce((acc, img) => {
      if (!acc[img.tipoImagem]) {
        acc[img.tipoImagem] = [];
      }
      acc[img.tipoImagem].push(img);
      return acc;
    }, {});

    // Reindexar as imagens por tipo
    const imagensReindexadas = Object.entries(imagensPorTipo).flatMap(([tipo, imgs]) => {
      // Ordenar as imagens do mesmo tipo pelo índice original
      const imgsOrdenadas = [...imgs].sort((a, b) => a.imagemIndice - b.imagemIndice);
      
      // Reindexar começando do 1
      return imgsOrdenadas.map((img, idx) => ({
        ...img,
        imagemIndice: idx + 1
      }));
    });

    // Ordenar por tipo e novo índice
    const imagensOrdenadas = imagensReindexadas.sort((a, b) => {
      const ordemA = ordemDesejada[a.tipoImagem] || 999;
      const ordemB = ordemDesejada[b.tipoImagem] || 999;
      
      if (ordemA === ordemB) {
        return a.imagemIndice - b.imagemIndice;
      }
      
      return ordemA - ordemB;
    });

    return imagensOrdenadas;
  };

  // Ordenar as imagens antes de renderizar
  const imagensOrdenadas = selectedImages ? ordenarImagens(selectedImages) : [];

  return (
    <StyledDialog open={open} maxWidth="lg" fullWidth onClose={onClose}>
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <GradientText variant="h6">Histórico de Imagens</GradientText>
        <IconButton
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
            },
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {isImagesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 7 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {imagensOrdenadas?.length ? imagensOrdenadas.map((image, index) => {
              const { tipoImagem, imagemIndice } = image;

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Fade
                    in
                    timeout={500}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: (theme) => `0 4px 20px 0 ${
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.1)'
                            : 'rgba(0, 0, 0, 0.3)'
                        }`,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: (theme) => `0 8px 24px 0 rgba(0, 0, 0, ${
                            theme.palette.mode === 'light' ? '0.15' : '0.4'
                          })`,
                        },
                        transition: 'all 0.3s ease',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        background: 'rgba(0, 0, 0, 0.7)',
                        padding: '4px 8px',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        borderRadius: '4px',
                        maxWidth: 'fit-content',
                      }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontWeight: 600,
                          }}
                        >
                          {`${tipoImagem} - Imagem ${imagemIndice}`}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          cursor: 'pointer',
                          position: 'relative',
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 1,
                        }}
                        onClick={() => onOpenZoom(image)}
                      >
                        <StyledImage
                          src={image.fotoLink}
                          alt={`Imagem ${index + 1}`}
                          style={{ maxHeight: '200px', objectFit: 'contain' }}
                        />
                      </Box>
                    </Box>
                  </Fade>
                </Grid>
              );
            }) : (
              <Box sx={{ py: 8, textAlign: 'center', mx: 'auto' }}>
                <Typography variant="p">
                  Nenhuma imagem encontrada no registro
                </Typography>
              </Box>
            )}
          </Grid>
        )}
      </DialogContent>
    </StyledDialog>
  );
};

export default HistoryDialog;
